import { API_GW_URL } from '../constants/variables';
import { getLiveTileEpoch } from './liveTileRefresh';
import { allLayersGroups } from '../constants/layers';

/**
 * Finds required query parameters for a given source URL
 * @param {string} url - The tile source URL
 * @returns {string[]} Array of required query parameter names
 */
const getRequiredQueryParams = url => {
    for (const layerGroup of allLayersGroups) {
        if (!layerGroup.layers) continue;

        for (const layer of layerGroup.layers) {
            if (layer.source?.url && url.includes(layer.source.url.split('?')[0])) {
                return layer.source.requiredQueryParams || [];
            }
        }
    }
    return [];
};

export const transformMapRequests = ({ idToken, userId, selectedDate, selectedScrTracksDate, selectedRebalanceDate, selectedRebalancePeriod, selectedRebalanceDayMode, mapFilters = {} }) => (url, resourceType) => {
    if ((resourceType === 'Source' || resourceType === 'Tile') && url.startsWith(API_GW_URL)) {
        const parsedUrl = new URL(url);

        parsedUrl.searchParams.set('userId', userId);

        // Живые слои (utils/liveTileRefresh.js): тот же _ts, что и у текущего источника, — иначе
        // тайлы придут из HTTP-кэша браузера и периодическое обновление ничего не покажет
        const liveTs = getLiveTileEpoch(url);
        if (liveTs) {
            parsedUrl.searchParams.set('_ts', String(liveTs));
        }

        // Get required query parameters for this layer's source
        const requiredParams = getRequiredQueryParams(url);
        // DA-14933 — у слоя «Треки исполнителей» отдельная дата (не общий selectedDate),
        // чтобы смена даты не триггерила перезапрос тайлов geo_starts_mvt
        const isScrTracks = url.includes('scr_tracks_mvt');

        // Add date if it's required for this layer and a date is available.
        // Для scr_tracks используем selectedScrTracksDate, для остальных — общий selectedDate.
        if (requiredParams.includes('date')) {
            const dateValue = isScrTracks ? selectedScrTracksDate : selectedDate;
            if (dateValue) {
                parsedUrl.searchParams.set('date', dateValue);
            }
        }

        // Add rebalance_date for fact rebalance layer
        if (requiredParams.includes('rebalance_date') && selectedRebalanceDate) {
            parsedUrl.searchParams.set('rebalance_date', selectedRebalanceDate);
            // В режиме period — передаём конкретный period_index; в режиме day — не передаём (NULL = суточный агрегат)
            if (selectedRebalanceDayMode === 'period' && Number.isInteger(selectedRebalancePeriod)) {
                parsedUrl.searchParams.set('period', String(selectedRebalancePeriod));
            }
        }

        // Add h3_zoom for aggTracksAugust layer (hist_aggregated_tracks_mvt)
        if (url.includes('hist_aggregated_tracks_mvt') && mapFilters.aggTracksAugustH3Zoom) {
            parsedUrl.searchParams.set('h3_zoom', mapFilters.aggTracksAugustH3Zoom);
        }

        // Add h3_zoom for adminAggregatedTracksV2 layer ('auto' means DB function uses z-1)
        if (url.includes('agg_hex_user_tracks_7days_mvt') && mapFilters.adminAggTracksV2H3Zoom) {
            parsedUrl.searchParams.set('h3_zoom', mapFilters.adminAggTracksV2H3Zoom);
        }

        // Add h3_zoom + month for adminAggregatedTracksMonthly layer (DA-14786)
        if (url.includes('agg_hex_user_tracks_monthly_mvt')) {
            if (mapFilters.adminAggTracksMonthlyH3Zoom) {
                parsedUrl.searchParams.set('h3_zoom', mapFilters.adminAggTracksMonthlyH3Zoom);
            }
            if (mapFilters.adminAggTracksMonthlyMonth) {
                parsedUrl.searchParams.set('month', mapFilters.adminAggTracksMonthlyMonth);
            }
        }

        // Add h3_zoom + period for aggregatedTracksCohort layer (DA-15259)
        if (url.includes('agg_hex_user_tracks_cohort_7days_mvt')) {
            if (mapFilters.aggTracksCohortH3Zoom) {
                parsedUrl.searchParams.set('h3_zoom', mapFilters.aggTracksCohortH3Zoom);
            }
            if (mapFilters.aggTracksCohortPeriod) {
                parsedUrl.searchParams.set('period', mapFilters.aggTracksCohortPeriod);
            }
        }

        // Add h3_zoom + period + dimension for userCohorts layer (DA-15415).
        // dimension определяет набор колонок в тайле, поэтому его смена перезагружает
        // тайлы (см. H3_RELOAD_CONFIGS в Map.jsx).
        if (url.includes('agg_hex_route_user_cohorts_7days_mvt')) {
            if (mapFilters.userCohortsH3Zoom) {
                parsedUrl.searchParams.set('h3_zoom', mapFilters.userCohortsH3Zoom);
            }
            if (mapFilters.userCohortsPeriod) {
                parsedUrl.searchParams.set('period', mapFilters.userCohortsPeriod);
            }
            if (mapFilters.userCohortsDimension) {
                parsedUrl.searchParams.set('dimension', mapFilters.userCohortsDimension);
            }
        }

        // Add h3_zoom for wifiAp h3 aggregate layer (DA-16000)
        if (url.includes('wifi_ap_h3_mvt') && mapFilters.wifiApH3Zoom) {
            parsedUrl.searchParams.set('h3_zoom', mapFilters.wifiApH3Zoom);
        }

        // Add h3_zoom for regToTripConversion layer (DA-13570)
        if (url.includes('good_bad_area_test_mvt') && mapFilters.regToTripH3Zoom) {
            parsedUrl.searchParams.set('h3_zoom', mapFilters.regToTripH3Zoom);
        }

        // Add h3_zoom for h3Grid layer (DA-13776)
        if (url.includes('h3_grid_mvt') && mapFilters.h3GridH3Zoom) {
            parsedUrl.searchParams.set('h3_zoom', mapFilters.h3GridH3Zoom);
        }

        // Add h3_zoom для lostDevicesH3 layer (DA-12966)
        if (url.includes('lost_devices_h3_mvt') && mapFilters.lostDevicesH3Zoom) {
            parsedUrl.searchParams.set('h3_zoom', mapFilters.lostDevicesH3Zoom);
        }

        // Add h3_zoom для walkscoreH3 layer (DA-14919); 'auto' → функция использует z-1
        if (url.includes('walkscore_h3_mvt') && mapFilters.walkscoreH3Zoom) {
            parsedUrl.searchParams.set('h3_zoom', mapFilters.walkscoreH3Zoom);
        }

        // Add h3_zoom и period для routeAnomaliesH3 layer (DA-14308)
        // Обрабатываем оба endpoint: from_h3 (откуда) и to_h3 (куда)
        if (url.includes('route_anomalies_from_h3_mvt') || url.includes('route_anomalies_to_h3_mvt')) {
            if (mapFilters.routeAnomaliesH3Zoom) {
                parsedUrl.searchParams.set('h3_zoom', mapFilters.routeAnomaliesH3Zoom);
            }
            parsedUrl.searchParams.set('period', mapFilters.routeAnomaliesPeriod || '1d');
        }

        return {
            url: parsedUrl.toString(),
            headers: {
                Authorization: idToken,
            },
        };
    }

    return { url };
};
